import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { shouldDenyWorkspacePermissionAccess } from '../lib/workspacePermissionGate';
import type { WorkflowGraphDto } from '@prometeus/contracts';
import {
  useWorkflowPreset,
  useUpdateWorkflowPreset,
} from '../hooks/useWorkflowPresets';
import {
  WorkflowPresetEditor,
  type WorkflowPresetEditorHandle,
} from '../components/workflow/WorkflowPresetEditor';
import { useConfirmDialog } from '../hooks/useConfirmDialog';

export const Route = createFileRoute(
  '/w/$workspaceId/workflow-presets/$presetId',
)({
  component: WorkflowPresetEditorPage,
});

function WorkflowPresetEditorPage() {
  const { workspaceId, presetId } = Route.useParams();
  const location = useLocation();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const navigate = useNavigate();
  const { confirm } = useConfirmDialog();

  const canAccess = hasPermission('workflow_presets.update');

  useEffect(() => {
    if (
      !shouldDenyWorkspacePermissionAccess({
        pathname: location.pathname,
        isLoadingPermissions,
        hasAccess: canAccess,
      })
    ) {
      return;
    }
    showPermissionDenied('Editar Workflow Presets');
    navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
  }, [
    location.pathname,
    isLoadingPermissions,
    canAccess,
    navigate,
    showPermissionDenied,
    workspaceId,
  ]);

  const { data: preset, isLoading, isError } = useWorkflowPreset(presetId);
  const updatePreset = useUpdateWorkflowPreset();

  const [localName, setLocalName] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<WorkflowPresetEditorHandle | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (preset) {
      setLocalName(preset.name ?? '');
    }
  }, [preset?.id, preset?.name]);

  if (!canAccess) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">
        Carregando workflow...
      </div>
    );
  }

  if (isError || !preset) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-6 text-center space-y-3">
          <p className="text-sm font-medium text-gray-800">
            Não foi possível carregar este Workflow Preset.
          </p>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: '/w/$workspaceId/workflow-presets',
                params: { workspaceId },
              })
            }
            className="inline-flex items-center rounded-full bg-[#06141B] px-4 py-1.5 text-xs font-medium text-white hover:bg-[#0b202b]"
          >
            Voltar para Workflow Presets
          </button>
        </div>
      </div>
    );
  }

  const handleSaveMeta = () => {
    if (!requirePermission('workflow_presets.update', 'Atualizar Workflow Preset')) return;
    updatePreset.mutate({
      id: presetId,
      payload: {
        name: localName.trim() || preset.name,
      },
    });
  };

  const handleExportJson = () => {
    const payload = {
      name: localName.trim() || preset.name,
      exportedAt: new Date().toISOString(),
      graph: preset.graph ?? { nodes: [], edges: [], notes: [] },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(localName.trim() || preset.name || 'workflow').replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const navigateToList = () =>
    navigate({
      to: '/w/$workspaceId/workflow-presets',
      params: { workspaceId },
    });

  const handleBack = async () => {
    if (!hasUnsavedChanges) {
      navigateToList();
      return;
    }

    const saveAndExit = await confirm({
      title: 'Você possui alterações não salvas',
      description: (
        <>
          Existem definições do workflow que ainda não foram salvas.
          <br />
          Deseja salvar e sair ou apenas sair do editor?
        </>
      ),
      confirmLabel: 'Salvar e sair',
      cancelLabel: 'Apenas sair',
      variant: 'warning',
    });

    if (saveAndExit) {
      if (
        !requirePermission(
          'workflow_presets.update',
          'Atualizar Workflow Preset',
        )
      ) {
        return;
      }
      const graph = editorRef.current?.getGraph();
      if (graph) {
        updatePreset.mutate(
          {
            id: presetId,
            payload: {
              graph,
            },
          },
          {
            onSuccess: () => {
              navigateToList();
            },
          },
        );
        return;
      }
    }

    navigateToList();
  };

  const handleImportJson = () => {
    importInputRef.current?.click();
  };

  const onImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const data = JSON.parse(text) as { name?: string; graph?: { nodes?: unknown[]; edges?: unknown[]; notes?: unknown[] } };
        const graph = data.graph;
        if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges)) {
          window.alert('Arquivo JSON inválido: esperado objeto com "graph" contendo "nodes" e "edges".');
          return;
        }
        const payload: WorkflowGraphDto = {
          nodes: graph.nodes as WorkflowGraphDto['nodes'],
          edges: graph.edges as WorkflowGraphDto['edges'],
          notes: Array.isArray(graph.notes) ? (graph.notes as WorkflowGraphDto['notes']) : [],
        };
        if (data.name != null && typeof data.name === 'string') {
          setLocalName(data.name);
          updatePreset.mutate({
            id: presetId,
            payload: { name: data.name, graph: payload },
          });
        } else {
          updatePreset.mutate({
            id: presetId,
            payload: { graph: payload },
          });
        }
      } catch {
        window.alert('Não foi possível ler o arquivo JSON.');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 min-w-0">
      <input
        ref={importInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={onImportFile}
      />
      <div className="flex-1 min-h-0 flex flex-col">
        <WorkflowPresetEditor
          ref={editorRef}
          value={preset.graph ?? null}
          onChange={async (graph) => {
            if (
              !requirePermission(
                'workflow_presets.update',
                'Atualizar Workflow Preset',
              )
            ) {
              throw new Error('Sem permissão para atualizar o workflow.');
            }
            await updatePreset.mutateAsync({
              id: presetId,
              payload: {
                graph,
              },
            });
          }}
          fullScreen
          onDirtyChange={setHasUnsavedChanges}
          topBar={{
            workflowName: localName,
            onBack: handleBack,
            onNameChange: setLocalName,
            onNameBlur: handleSaveMeta,
            onImportClick: handleImportJson,
            onExportClick: handleExportJson,
            isActive: preset.isActive,
          }}
        />
      </div>
    </div>
  );
}

